import * as React from "react";
import GlobalStyles from "@mui/joy/GlobalStyles";
import Box from "@mui/joy/Box";
import List from "@mui/joy/List";
import { listItemButtonClasses } from "@mui/joy/ListItemButton";
import Sheet from "@mui/joy/Sheet";
import { useDispatch, useSelector } from "react-redux";
import { useUiStateSlice } from "store/UIStateStore/slice";
import { selectUiState } from "store/UIStateStore/slice/selectors";
import SidebarItem from "./SidebarItem";
import { closeSidebar } from "app/utils";
import { useMenuList } from "./utils/useMenuList";
import { useMediaQuery } from "@mui/material";

export default function SidebarNav() {
  const dispatch = useDispatch();
  const { menus } = useMenuList()
  const { actions: uiStateActions } = useUiStateSlice();
  const uiStateStore = useSelector(selectUiState);
  const isMediaMD = useMediaQuery((theme:any) => theme?.breakpoints?.up('md'));
  const { isSidebarOpen = isMediaMD } = uiStateStore;

  // Listen for sidebar toggle events to ensure state synchronization
  React.useEffect(() => {
    const handleSidebarToggle = (event: Event) => {
      const customEvent = event as CustomEvent;
      const isOpen = customEvent.detail?.isOpen;

      if (isOpen !== undefined && isOpen !== isSidebarOpen) {
        dispatch(uiStateActions.toggleSidebar());
      }
    };

    document.addEventListener("sidebarToggle", handleSidebarToggle);

    return () => {
      document.removeEventListener("sidebarToggle", handleSidebarToggle);
    };
  }, [dispatch, isSidebarOpen, uiStateActions]);

  React.useEffect(()=>{

    if(!isMediaMD){
      dispatch(uiStateActions.setIsSidebarOpen(false));
    }

  },[])


  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: "fixed", md: "sticky" },
        transform: {
          xs: isSidebarOpen ? "translate(0, 64px)" : "translate(-100%, 64px)",
          md: "none",
        },
        left: { xs: 0, sm: 0, md: "auto" },
        zIndex: 100,
        height: "100%",
        top: 0,
        p: 1,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderRight: "1px solid",
        borderColor: "divider",
        width: {
          xs: isSidebarOpen ? "var(--Sidebar-width)" : '80px',
          md: isSidebarOpen ? "var(--Sidebar-width)" : "80px",
        },
        maxWidth: 'unset !important',
        transition: "width 0.3s ease-in-out, transform 0.3s ease-in-out",
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ":root": {
            "--Sidebar-width": "240px",
          },
          // Add responsive styles for the sidebar
          ".Sidebar": {
            [theme.breakpoints.down("md")]: {
              "&.MuiSheet-root": {
                // Ensure sidebar stays within viewport on small screens
                maxWidth: "80px",
              },
            },
          },
          // Fix for unused space on the right in small screens
          body: {
            overflowX: "hidden",
            ".MainContent": {
              [theme.breakpoints.down("sm")]: {
                paddingRight: 0,
                marginRight: 0,
                width: "100%",
                maxWidth: "100vw",
              },
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: "fixed",
          zIndex: 0,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: "var(--SideNavigation-slideIn)",
          transition: "opacity 0.4s",
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
            lg: "translateX(-100%)",
          },
          display: { md: "none" }, // Only show overlay on mobile
          bgcolor: "rgba(0, 0, 0, 0.3)", // Add semi-transparent background
        }}
        onClick={() => closeSidebar()}
      />
      <Box
        sx={{
          minHeight: 0,
          overflow: "hidden auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: 1,
            "--List-nestedInsetStart": "30px",
            "--ListItem-radius": (theme) => theme.vars.radius.sm,
          }}
        >
          {
            menus?.length > 0 &&
            menus.map((item, index) => (
              <SidebarItem
                key={index}
                onClick={item.onClick}
                href={item.href}
                text={item.text}
                icon={item.icon}
                isSidebarOpen={isSidebarOpen}
              />
            ))}
        </List>
      </Box>
    </Sheet>
  );
}
